import React, { useEffect, useMemo } from "react";
import {
  useFittingProduct,
  useProductLabels,
  useProductSpecsExtended,
  useUserProducts
} from "../../hooks/ProductHooks";
import Page from "../../layout/Page";
import GenericOverlay from "../../components/modal/GenericOverlay";
import PageNotFoundFragment from "../../fragments/PageNotFoundFragment";
import { gtmSendEvent, gtmSendViewItem } from "../../utils/GTMUtils";
import {
  productIsPerSquareMeter,
  productIsUnavailable,
  productPaths,
  productPriceUnit,
  productUrl,
  productUrls
} from "../../utils/ProductUtils";
import { Helmet } from "react-helmet";
import { useLang, usePathLang } from "../../hooks/LangHooks";
import { useProductCategories } from "../../hooks/ProductCategoryHooks";
import {
  faqBaseUrl,
  guidesProductUrl,
  shopUrl,
  toAbsoluteUrl
} from "../../config/urls";
import {
  useBjelinTranslations,
  useProductTranslations,
  useSpecsTranslation,
  useUnitTranslation
} from "../../hooks/TranslationHooks";
import {
  useLocSettings,
  useProductTypeById,
  useSetting
} from "../../hooks/SettingsHooks";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import ContentSection from "../../components/ContentSection";
import Gallery from "../../components/Gallery";
import { cmsUrl, transformHtml } from "../../utils/CmsUtils";
import Breadcrumbs from "../../components/Breadcrumbs";
import { Col, Row } from "reactstrap";
import ProductSpecification from "../../components/ProductSpecification";
import ProductDocuments from "../../components/ProductDocuments";
import ProductLabels from "../../components/ProductLabels";
import CareInstructionModal from "../../components/modal/CareInstructionModal";
import ProductSchema from "../../components/ProductSchema";
import CockpitProductGrid from "../../components/CockpitLayoutComponents/CockpitProductGrid";
import { filterProductsByIds } from "../../utils/ProductsUtils";
import BjelinButton from "../../components/BjelinButton";
import { useUser } from "../../functional-components/UserContext";
import GuideListing from "../../components/Guide/GuideListing";
import { pdsUrl } from "../../config/apiUrls";
import PriceArea from "../../components/ProductPurchaseColumn/PriceArea";
import FloatingProductPurchaseArea from "../../components/ProductPurchaseColumn/floating";
import SalesRegionsArea from "../../components/ProductPurchaseColumn/SalesRegionsArea";
import ProductFaq from "../../components/FAQ/ProductFaq";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import Icon from "../../components/Icon";
import ProductStock from "../../components/ProductStock";
import ProductReplacedByArea from "../../components/ProductPurchaseColumn/ProductReplacedByArea";
import ExpandableContent from "../../components/ExpandableContent";
import { uniqBy } from "lodash";
import { cockpitComponent } from "../../components/CockpitLayoutComponents";
import StoryGrid from "../../components/StoryGrid";
import { Flags } from "../../components/Flags";
import CockpitEditLink from "../../components/CockpitEditLink.jsx";
import { getCurrentSite } from "../../utils/SiteUtils";
import {
  getProductCategoryPedigree,
  productCategoryTitle,
  productCategoryUrl,
  useCategoryByProduct,
  useCategoryFromPath
} from "../../utils/ProductCategoryUtils";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import SectionNavigation from "../../components/SectionNavigation";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";
import classNames from "classnames";
import IconWithPopup from "../../components/IconWithPopup";
import FormattedPrice from "../../components/FormattedPrice";
import { addVat } from "../../utils/VatUtils";
import LabelIcons from "../../components/ProductSpecification/icons";
import { useStories } from "../../hooks/CmsHooks";
import "./style.scss";
import SpecsSummary from "../../components/ProductSpecification/summary";
import { useFaqData } from "../../hooks/FaqHooks";
import SizeFloor from "../../components/SizeFloor";
import { useMediaQuery } from "react-responsive";
import OrderSampleColumn from "../../components/ProductPurchaseColumn/OrderSampleColumn";
import { addNoOverlayState } from "../../utils/UrlUtils";
import {
  useAccessoriesFor,
  useCompatibleWith
} from "../../hooks/AccessoryRulesHooks";

const ProductPage = ({
  artId,
  displayAsOverlay = false,
  isPreview = false
}) => {
  console.log("ProductPage", artId);
  return (
    <PageWrapper displayAsOverlay={displayAsOverlay} key={artId}>
      <ProductPageContent
        artId={artId}
        displayAsOverlay={displayAsOverlay}
        isPreview={isPreview}
        key={artId}
      />
    </PageWrapper>
  );
};

const PageWrapper = ({ displayAsOverlay, children }) => {
  return displayAsOverlay ? (
    <GenericOverlay hasSectionNav id="product-overlay" key="product-overlay">
      {children}
    </GenericOverlay>
  ) : (
    children
  );
};

const ProductPageContent = ({
  artId: art_id,
  isPreview = false,
  displayAsOverlay = false
}) => {
  const location = useLocation();
  const pathLang = usePathLang();
  const pathname = location.pathname;
  const artId = art_id;

  const product = useFittingProduct(artId);
  const categories = useProductCategories();
  //console.log("product:", product);
  //console.log("categories:", categories);
  const validProductUrls = productUrls(product, categories);
  //console.log("validProductUrls:", validProductUrls);
  const redirectTo =
    validProductUrls.length > 0 && validProductUrls.indexOf(pathname) === -1
      ? validProductUrls[0]
      : "";

  useEffect(() => {
    if (product) {
      gtmSendViewItem(product, categories);
    }
  }, [categories, product]);

  if (!product) {
    return displayAsOverlay ? (
      <GenericOverlay id="product-overlay">
        <PageNotFoundFragment />
      </GenericOverlay>
    ) : (
      <Page
        title={""}
        name={"404"}
        key={"product-page" + artId}
        transition="simple"
      >
        <PageNotFoundFragment />
      </Page>
    );
  }

  if (redirectTo) {
    return (
      <Navigate
        to={{
          pathname: redirectTo,
          search: location.search,
          hash: location.hash
        }}
        replace
        state={addNoOverlayState()}
      />
    );
  }

  return (
    <>
      <Helmet>
        <link
          rel="canonical"
          href={toAbsoluteUrl(validProductUrls[0], pathLang)}
        />
      </Helmet>
      <ProductPageInner2
        product={product}
        location={location}
        displayAsOverlay={displayAsOverlay}
        isPreview={isPreview}
      />
    </>
  );
};

const ContentWrapper = ({ title, artId, displayAsOverlay, children }) => {
  return displayAsOverlay ? (
    children
  ) : (
    <Page
      name={"product-page"}
      key={"product-page" + artId}
      title={title}
      transition={"simple"}
    >
      <div className="content-padding-for-menu-bar" />
      {children}
    </Page>
  );
};

export const ProductPageInner2 = ({ product, displayAsOverlay = false }) => {
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  const lang = useLang();
  const { admin, b2b } = useUser();
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const features = useSiteFeatures();
  const products = useUserProducts();
  const categoryFromProduct = useCategoryByProduct(product);
  const params = useParams();
  const categoryFromPath = useCategoryFromPath(params.category);
  const category = categoryFromPath || categoryFromProduct;
  const productCategoryPedigree = getProductCategoryPedigree(category);
  const {
    _id: CockpitId,
    name,
    sku,
    productType: productTypeId,
    description,
    images,
    documents,
    care_instruction: careInstructionContent,
    saleable,
    sampleSaleable
  } = product;
  const purchasable = features.shop() && saleable;
  const onlySamplePurchasable = features.shop() && !saleable && sampleSaleable;
  const productType = useProductTypeById(productTypeId);
  const fittingAccessories = useAccessoriesFor(sku);
  const fittingAccessoryTargets = useCompatibleWith(sku);
  const _stories = useStories();

  const hasPrice = !!product?.price?.basePrice;

  const storyCount = useMemo(() => {
    return _stories.filter(
      story =>
        Array.isArray(story.products) &&
        story.products.findIndex(prod => CockpitId === prod._id) >= 0
    ).length;
  }, [_stories, CockpitId]);

  const _faqData = useFaqData(lang);
  const faqCount = useMemo(() => {
    return _faqData.articles.filter(
      article =>
        (productTypeId && article.product_types.indexOf(productTypeId) >= 0) ||
        article.products.indexOf(product._id) >= 0
    ).length;
  }, [_faqData.articles, productTypeId, product._id]);

  const allDocuments = uniqBy(
    documents.concat(productType?.documents || []),
    "_id"
  );

  const hasProductDocuments = (allDocuments || []).length > 0;
  const hasCareInstructions = !!careInstructionContent;

  useEffect(() => {
    const paths = productPaths(product, category);
    registerCurrentPaths(paths);
    setAvailableOnSites(product.sites);
  }, [registerCurrentPaths, setAvailableOnSites, product, category]);

  const title = productIsUnavailable(product)
    ? `${tp("shipping_status.unavailable").toUpperCase()} ${name}`
    : name;

  const backgroundImage =
    category?.is_floor && images[0]
      ? cmsUrl(images[0].path)
      : category?.path === "outlet"
      ? "http://cdn.bjelin.io/storage/uploads/2021/11/19/Outlet-bkg-4-ljus_uid_6197aadbcc504.jpg"
      : false;
  const breadcrumbItems = [
    features.shop()
      ? { url: shopUrl, text: t("shop") }
      : { url: shopUrl, text: t("menu-label.products") }
  ];
  productCategoryPedigree.forEach(cat => {
    breadcrumbItems.push({
      url: productCategoryUrl(cat),
      text: productCategoryTitle(cat, tp)
    });
  });

  const accessoryPriorities = [
    "6155c814193f33619c0f2084", // Start triangles
    "5ef5bf1a97f3bc3136395719", // Skirting
    "6602cfff40369a1daf638372", // Stair noses
    "67596ee1b55631824d005c12", // Moulding
    "671f8a4dc57d1a9c3a0bcbc2", // Thresholds
    "5ef5bfb847e0f461126972e3", // Underlay
    "5ef5bef4d1528e2c8e31c744", // Tools
    "668294d2be387569a9500742", // Protection
    "5ef5fa478c460259203be082", // Oil and wax
    "5ef5fa0cd3471a120656d932", // Cleaning
    "5ef5fa67ae69f1395d207582", // Varnish
    "5ef5bef4d1528e2c8e31c744" // Tools
  ];
  const accessoriesToList = filterProductsByIds(
    products,
    fittingAccessories.map(acc => acc.id)
  )
    .sort(
      (a, b) =>
        (a.categories?.[0]
          ? accessoryPriorities.indexOf(a.categories?.[0])
          : 9999) -
        (b.categories?.[0]
          ? accessoryPriorities.indexOf(b.categories?.[0])
          : 9999)
    )
    .map(p => p.sku);
  const accessoriesReversedToList = filterProductsByIds(
    products,
    fittingAccessoryTargets.map(acc => acc.id)
  ).map(p => p.sku);

  return (
    <ContentWrapper
      backgroundImage={backgroundImage}
      title={title}
      artId={product.sku}
      b2b={b2b}
      displayAsOverlay={displayAsOverlay}
      key={sku}
    >
      <ProductSchema product={product} lang={lang} />
      {features.shop() && saleable && (
        <FloatingProductPurchaseArea sku={sku}></FloatingProductPurchaseArea>
      )}

      <SectionNavigation
        direction={"vertical"}
        showMenu
        showMenuExpandButton
        inOverlay={displayAsOverlay}
      >
        <ContentSection
          navTitle={tp("nav-title.description")}
          navID="description"
        >
          <Breadcrumbs items={breadcrumbItems} />
          <MainHeading product={product} />
          <div className="product-header">
            <div className="product-header-main">
              <GalleryColumn product={product} category={category} />
              <ProductReplacedByArea
                product={product}
                className="product-replaced-by"
              />
            </div>
            <div className="product-header-aside">
              <IconsAndTagline product={product} />
              {product.variantGroup?.products?.length > 0 &&
                product.variantGroup?.specifications?.length > 0 && (
                  <VariantColumn
                    sku={sku}
                    product={product}
                    products={products}
                    variantGroup={product.variantGroup}
                    category={category}
                    displayAsOverlay={displayAsOverlay}
                  />
                )}
              {((features.shop() && saleable) ||
                (!features.shop() && features.prices())) &&
                hasPrice && <PriceArea product={product} />}
              {onlySamplePurchasable && <OrderSampleColumn product={product} />}
              {!purchasable && <ContactUsColumn product={product} />}
              <div className={"product-purchase-column"}>
                <p className={"article-number"}>
                  {tp("article_number")}: {sku}
                  <CockpitEditLink
                    collection={"products"}
                    _id={product._id}
                    labelKey={" "}
                    style={{ color: "var(--bs-pink)" }}
                    wrapper={children => children}
                  />
                  {admin && (
                    <a
                      rel={"noreferrer"}
                      target={"_blank"}
                      href={`https://bjel.in/info/${getCurrentSite()}/art/${sku}`}
                      className={"text-black ms-1"}
                    >
                      <Icon name={"qrcode"} />
                    </a>
                  )}
                </p>
                {admin && product.tags && (
                  <p className={"article-tags"}>
                    {product.tags.map(tag => (
                      <span key={tag} style={{ marginRight: "1em" }}>
                        <Icon type={"far"} name={"tag"} /> {tag}
                      </span>
                    ))}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className={"product-description"}>
            {transformHtml(description)}
          </div>
        </ContentSection>

        {accessoriesReversedToList.length > 0 && (
          <ContentSection
            fadeIn
            theme={"grey"}
            className={"mb-3"}
            navTitle={tp("nav-title.fits")}
            navID="fits"
          >
            <Row>
              <Col>
                <h3>{t("product.recommended.reversed.accessories")}</h3>
                <ExpandableContent
                  disabled={accessoriesReversedToList.length <= 3}
                  backgroundColor="#dfe2e1"
                  initialHeight="16.4em"
                >
                  <CockpitProductGrid
                    skus={accessoriesReversedToList}
                    layout={"list"}
                    className="accessories-list"
                  />
                </ExpandableContent>
              </Col>
            </Row>
          </ContentSection>
        )}

        <ContentSection
          navTitle={tp("nav-title.specs")}
          navID="specs"
          theme={"sand"}
          className={"product-specification mb-3"}
          style={{ padding: "1em 0 2em" }}
        >
          <SpecsColumn product={product} />
        </ContentSection>

        {(hasProductDocuments || hasCareInstructions) && (
          <ContentSection
            navTitle={tp("nav-title.docs")}
            navID="docs"
            theme={"sand"}
            className={"product-specification mb-3"}
            style={{ padding: "1em 0 4em" }}
          >
            <DocsColumn product={product} />
          </ContentSection>
        )}

        {storyCount > 0 && (
          <ContentSection
            theme={"white"}
            noMargins
            navTitle={tp("nav-title.stories")}
            navID="stories"
          >
            <StoryGrid
              productCockpitIds={[CockpitId]}
              header={storiesCount =>
                storiesCount ? (
                  <ContentSection
                    theme={"white"}
                    style={{ textAlign: "center" }}
                  >
                    <h2>{t("stories.related")}</h2>
                  </ContentSection>
                ) : null
              }
            />
          </ContentSection>
        )}

        {accessoriesToList.length > 0 && (
          <ContentSection
            fadeIn
            theme={"grey"}
            className={"mb-3"}
            navTitle={tp("nav-title.accessories")}
            navID="accessories"
            style={{ padding: "1em 0" }}
          >
            <Row>
              <Col>
                <h3>{t("product.recommended.accessories")}</h3>
                <ExpandableContent
                  disabled={accessoriesToList.length <= 3}
                  backgroundColor="#dfe2e1"
                  initialHeight="36.5em"
                >
                  <CockpitProductGrid
                    skus={accessoriesToList}
                    layout={"list"}
                    showCartButtons
                    className="accessories-list"
                  />
                </ExpandableContent>
              </Col>
            </Row>
          </ContentSection>
        )}

        {faqCount > 0 && (
          /* Because SectionNavigation parses React nodes before they are rendered, we currently need checks like this, and cannot let a sub component return a ContentSection. */
          <ContentSection
            navTitle={tp("nav-title.faq")}
            navID="faq"
            style={{ padding: "1em 0 4em" }}
          >
            <ProductFaq product={product} />
          </ContentSection>
        )}

        {productType?.product_page_content && (
          <ContentSection navTitle={tp("nav-title.more")} navID="more">
            {productType?.product_page_content?.map(cockpitComponent)}
          </ContentSection>
        )}
      </SectionNavigation>
    </ContentWrapper>
  );
};

const GalleryColumn = ({ product, category }) => {
  const { sku, images } = product;

  return (
    <Gallery
      artId={sku}
      images={images}
      containImage={!category?.is_floor}
      has3DView={product.has_3d_view}
      startIndex={1}
    >
      <Flags product={product} inline={false} />
    </Gallery>
  );
};

const MainHeading = ({ product }) => {
  const { name, sku } = product;
  const { b2b } = useUser();

  return (
    <h1 className={"product-name"}>
      {b2b && <span className="text-pale">{sku} </span>}
      <span>{name}</span>
    </h1>
  );
};

const IconsAndTagline = ({ product }) => {
  const navigate = useNavigate();
  const t = useBjelinTranslations();
  const { coreSpecsIndexed, iconSpecsIndexed } =
    useProductSpecsExtended(product);

  //Limit number of icons to 4
  const iconsLength = Object.entries(iconSpecsIndexed).length;
  const iconSpecsIndexedLimited =
    iconsLength > 4
      ? Object.fromEntries(Object.entries(iconSpecsIndexed).slice(0, 4))
      : iconSpecsIndexed;
  const coreSpecsIndexedExtended =
    iconsLength > 4
      ? {
          ...Object.fromEntries(Object.entries(iconSpecsIndexed).slice(4)),
          ...coreSpecsIndexed
        }
      : coreSpecsIndexed;

  return (
    <>
      {product.tagline && (
        <div style={{ fontSize: "1.25em", marginBottom: "2rem" }}>
          {product.tagline}
        </div>
      )}
      {coreSpecsIndexed && Object.entries(coreSpecsIndexed).length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <div className={"feature-icons"}>
            <LabelIcons specsIndexed={iconSpecsIndexedLimited} />
          </div>
          <SpecsSummary specsIndexed={coreSpecsIndexedExtended} />
          <div style={{ fontSize: "0.85em", marginTop: "0.1em" }}>
            <a
              href="#specs"
              onClick={e => {
                e.preventDefault();
                navigate({ hash: "specs" }, { replace: true });
              }}
            >
              {t("specifications.show-more")}
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const ContactUsColumn = ({ product }) => {
  const { b2b } = useUser();
  const features = useSiteFeatures();
  const settings = useLocSettings();
  const t = useBjelinTranslations();
  return (
    <>
      {features.salesRegions() && <SalesRegionsArea product={product} />}
      <div
        className={"contact-us-column mb-3"}
        style={{ display: "flex", flexDirection: "column" }}
      >
        {settings?.footer?.samples_email ? (
          <BjelinButton
            onClick={() => {
              gtmSendEvent({
                event: "contact",
                bjelinContactData: {
                  source: "mail-button"
                }
              });
              window.location.href = "mailto:" + settings.footer.samples_email;
            }}
            color={"bjelin-green"}
            block={true}
            className={"mb-2 contact-us-mail-btn"}
            icon={"envelope-open"}
            icon_position={"before"}
          >
            {t(
              "product.contact-sample.button.label",
              settings.footer.samples_email
            )}
          </BjelinButton>
        ) : (
          settings?.footer?.contact_email && (
            <BjelinButton
              onClick={() => {
                gtmSendEvent({
                  event: "contact",
                  bjelinContactData: {
                    source: "mail-button"
                  }
                });
                window.location.href =
                  "mailto:" + settings.footer.contact_email;
              }}
              color={"bjelin-green"}
              block={true}
              className={"mb-2 contact-us-mail-btn"}
              icon={"envelope-open"}
              icon_position={"before"}
            >
              {t("product.contact.button.label", settings.footer.contact_email)}
            </BjelinButton>
          )
        )}
        {!product.exclusive && !b2b && (
          <BjelinButton
            url={"/map"}
            color={"bjelin-green"}
            block={true}
            className={"mb-2 contact-us-map-btn"}
            icon={"map-location-dot"}
            icon_position={"before"}
            subtitle={t("product.contact.find-us.subtitle")}
          >
            {t("product.contact.find-us")}
          </BjelinButton>
        )}
        {b2b && (
          <ProductStock
            className={"b2b-catalog-product-stock"}
            product={product}
            b2b={b2b}
          />
        )}
      </div>
    </>
  );
};

const VariantColumn = ({
  sku,
  product,
  products,
  category,
  variantGroup,
  displayAsOverlay
}) => {
  const showPricesWithVat = useShowPricesWithVat();
  const { unitQuantityFactor = 0 } = product;
  const isPerSquareMeter = productIsPerSquareMeter(unitQuantityFactor);
  const priceUnit = productPriceUnit(unitQuantityFactor);
  const features = useSiteFeatures();

  const allSpecs = useSetting("specifications");
  const allSpecsFlat = Object.entries(allSpecs).map(([name, values]) => ({
    ...values,
    name
  }));
  const variantProducts = filterProductsByIds(products, variantGroup.products);

  const variants = variantProducts.map(p => {
    const specs = variantGroup.specifications.map(spId => {
      const spec = allSpecsFlat.find(sp => sp._id === spId);
      return spec ? { ...spec, value: p.specsIndexed?.[spec.name]?.value } : {};
    });
    return { product: p, specs };
  });
  const ts = useSpecsTranslation();
  const tu = useUnitTranslation();
  const settings = useLocSettings();
  const valueOrder = settings.specification_value_order;

  //console.log("variants", variants, valueOrder);

  return (
    <>
      {variants?.[0].specs.map(spg => (
        <div className="product-variants" key={spg.key}>
          {spg.key ? (
            <>
              <h4 id={"spec-header-" + ts(spg.name)} className="spec-header">
                {ts(spg.name)}
              </h4>
              <div
                className="variant-list"
                role="listbox"
                aria-labelledby={"spec-header-" + ts(spg.name)}
              >
                {variants
                  .filter(v =>
                    v.specs.length > 1
                      ? v.specs.findIndex(
                          spp =>
                            spp.key !== spg.key &&
                            spp.value === product.specsIndexed[spp.key]?.value
                        ) > -1
                      : true
                  )
                  .sort((a, b) => {
                    const spA = a.specs.find(sp => sp.key === spg.key);
                    const spB = b.specs.find(sp => sp.key === spg.key);
                    //console.log("sort", spA.value, spB.value);
                    return (
                      1000000 *
                        (valueOrder[spg.key]
                          ? valueOrder[spg.key].indexOf(spA.value) -
                            valueOrder[spg.key].indexOf(spB.value)
                          : spA.value.localeCompare(spB.value)) +
                      parseInt(b.product.sku) -
                      parseInt(a.product.sku)
                    );
                  })
                  .map(v => {
                    const sp = v.specs.find(sp => sp.key === spg.key);
                    return sp.value ? (
                      <Link
                        role="listitem"
                        key={v.product._id}
                        to={productUrl(v.product, category)}
                        replace
                        state={
                          displayAsOverlay === false ? addNoOverlayState() : {}
                        }
                        title={v.product.name}
                        className={classNames({
                          active: v.product.sku === sku
                        })}
                      >
                        {sp.icons[sp.value] ? (
                          <IconWithPopup
                            field={sp.name}
                            value={sp.value}
                            unit={sp.unit}
                            description={sp.description}
                            key={sp.name + "_" + sp.value}
                            noTooltip
                            color={
                              ["Color", "Staining"].includes(sp.name)
                                ? undefined
                                : "#ffffff"
                            }
                          />
                        ) : (
                          <span
                            className="spec-icon"
                            style={{
                              fontSize: "2em",
                              fontWeight: "700",
                              height: "64px",
                              lineHeight: "64px"
                            }}
                          >
                            {sp.value}
                          </span>
                        )}
                        <div className={"spec-label"}>
                          {ts(sp.name, sp.value)} {sp.unit && tu(sp.unit)}
                        </div>
                        {features.shop() && v.product?.price?.basePrice && (
                          <div className={"price"}>
                            {isPerSquareMeter &&
                            !!v.product?.price?.unitPrice ? (
                              <FormattedPrice
                                price={
                                  showPricesWithVat
                                    ? addVat(v.product?.price?.unitPrice)
                                    : v.product?.price?.unitPrice
                                }
                                price_unit={priceUnit}
                                currency={v.product?.price?.currency}
                                price_class_name="big-price"
                              />
                            ) : (
                              <FormattedPrice
                                inclVat={showPricesWithVat}
                                price={
                                  showPricesWithVat
                                    ? addVat(v.product?.price?.basePrice)
                                    : v.product?.price?.basePrice
                                }
                                sales_unit={priceUnit}
                                currency={v.product?.price?.currency}
                                price_class_name="big-price"
                              />
                            )}
                          </div>
                        )}
                      </Link>
                    ) : (
                      console.error(
                        "Missing variant specs value for: " +
                          spg.key +
                          " on " +
                          v.product.name
                      )
                    );
                  })}
              </div>
            </>
          ) : (
            console.error("Unknown variant specs key: " + spg.key)
          )}
        </div>
      ))}
    </>
  );
};

const SpecsColumn = ({ product }) => {
  const { sku, specifications, specsIndexed, specsGrouped } = product;

  const lang = useLang();
  const site = getCurrentSite();
  const productLabels = useProductLabels(product);
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const isSmall = useMediaQuery({ maxWidth: 575.98 });

  const hasProductLabels = productLabels.length > 0;

  return (
    specifications.length > 0 && (
      <Row>
        <Col style={{ position: "relative" }}>
          <div
            className="cockpit-bjelin_button"
            style={{
              position: "absolute",
              top: 0,
              right: "1rem",
              marginTop: 0
            }}
          >
            <a
              className="button button-primary"
              href={pdsUrl(sku, site, lang)}
              title={isSmall ? t("print.product-document") : undefined}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="download" />
              {!isSmall && <> {t("print.product-document")}</>}
            </a>
          </div>
          <h3>{tp("specification")}</h3>
          <div className={"specs-columns mb-3"}>
            <ProductSpecification
              specsGrouped={specsGrouped}
              specsIndexed={specsIndexed}
            />
            <div className="avoid-break">
              <SizeFloor className={"sizefloor"} specsIndexed={specsIndexed} />
              {hasProductLabels && (
                <ProductLabels
                  labels={productLabels}
                  specsIndexed={specsIndexed}
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
    )
  );
};

const DocsColumn = ({ product }) => {
  const {
    sku,
    productType: productTypeId,
    documents,
    care_instruction: careInstructionContent
  } = product;

  const lang = useLang();
  const features = useSiteFeatures();
  const faq_product_sections = useSetting("faq_product_section");
  const productType = useProductTypeById(productTypeId);
  const t = useBjelinTranslations();
  const tp = useProductTranslations();

  const FAQ_PRODUCT_SECTION_ID = faq_product_sections?.[lang];
  const allDocuments = uniqBy(
    documents.concat(productType?.documents || []),
    "_id"
  );
  const hasProductDocuments = (allDocuments || []).length > 0;
  const hasCareInstructions = !!careInstructionContent;

  return (
    (hasProductDocuments || hasCareInstructions) && (
      <Row>
        <Col>
          <h3>
            {tp(features.guides() ? "guides.and.documents" : "documents")}
          </h3>
          {features.guides() && (
            <GuideListing
              tags={product.tags}
              pathPrefix={guidesProductUrl(sku)}
            />
          )}
          {hasProductDocuments && <ProductDocuments documents={allDocuments} />}
          {hasCareInstructions && (
            <CareInstructionModal content={careInstructionContent} />
          )}
          {hasProductDocuments && FAQ_PRODUCT_SECTION_ID && (
            <ul className="guide-listing">
              <li>
                <Link
                  className={"document"}
                  to={`${faqBaseUrl}/${FAQ_PRODUCT_SECTION_ID}/installation`}
                >
                  <Icon name="circle-question" />{" "}
                  {t("faq.installation-questions")}
                </Link>
              </li>
            </ul>
          )}
        </Col>
      </Row>
    )
  );
};

export default ProductPage;
